/* Stav hypotečního dotazníku a maskování citlivých údajů — čisté funkce.
 *
 * PROČ SAMOSTATNÝ MODUL:
 *  1) Poradce potřebuje stav poznat na první pohled (neodesláno / čeká /
 *     rozpracováno / vyplněno). Odvození stavu je rozhodovací strom s časem
 *     a snadno se v něm udělá chyba — patří do testovatelné funkce, ne do JSX.
 *  2) Maskování rodného čísla se NESMÍ dělat ad hoc v komponentě. Kdyby se na
 *     jednom místě zapomnělo, svítí celostátní identifikátor klienta na
 *     obrazovce poradce, kterou na schůzce často otáčí ke klientovi nebo sdílí
 *     na videohovoru. Jedna funkce = jedno místo, kde se to dá pokazit.
 *
 * ŽÁDNÁ VYMYŠLENÁ DATA: když server nepošle počty polí, procenta jsou null a
 * UI napíše jen „rozpracováno“ bez čísla. Vymyšlené 0 % nebo 50 % by poradce
 * četl jako fakt o klientovi.
 */

/** Stavy, které může dotazník mít. Vypršení a zneplatnění jsou samostatné
 *  stavy — bez nich by poradce viděl „odesláno a čeká“ u odkazu, který se
 *  klientovi už neotevře, a divil by se, proč klient nereaguje. */
export const STAV = {
  NEODESLANO: "neodeslano",
  CEKA: "ceka",
  ROZPRACOVANO: "rozpracovano",
  VYPLNENO: "vyplneno",
  VYPRSEL: "vyprsel",
  ZNEPLATNEN: "zneplatnen",
};

/* Popisky. Barvy jsou tokeny designu, ne natvrdo hex — kdyby se změnil motiv,
   nesmí zůstat semafor ve staré paletě. */
export const STAV_POPIS = {
  [STAV.NEODESLANO]: { cs: "Neodesláno", sk: "Neodoslané", barva: "var(--muted)", tlumene: true },
  [STAV.CEKA]: { cs: "Odesláno a čeká", sk: "Odoslané a čaká", barva: "var(--accent)" },
  [STAV.ROZPRACOVANO]: { cs: "Rozpracováno klientem", sk: "Rozpracované klientom", barva: "var(--accent)" },
  [STAV.VYPLNENO]: { cs: "Vyplněno", sk: "Vyplnené", barva: "var(--positive)" },
  [STAV.VYPRSEL]: { cs: "Odkaz vypršel", sk: "Odkaz vypršal", barva: "#C53030" },
  [STAV.ZNEPLATNEN]: { cs: "Odkaz zneplatněn", sk: "Odkaz zneplatnený", barva: "#C53030" },
};

/** Datum z ISO stringu, nebo null. Nikdy nevrací Invalid Date. */
export function datum(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Procenta vyplnění z reálných počtů polí.
 * Vrací null, když počty neznáme — volající pak nesmí ukázat žádné číslo.
 */
export function procentaVyplneni(polozka) {
  const celkem = Number(polozka?.celkem_poli);
  const vyplnenych = Number(polozka?.vyplnenych_poli);
  if (!Number.isFinite(celkem) || celkem <= 0) return null;
  if (!Number.isFinite(vyplnenych) || vyplnenych < 0) return null;
  /* Strop na 100 %: kdyby server někdy počítal pole jinak než definice,
     ať se poradci neukáže „112 % vyplněno“. */
  const pct = Math.round((Math.min(vyplnenych, celkem) / celkem) * 100);
  return pct;
}

/**
 * Odvodí stav dotazníku.
 *
 * @param {object|null} polozka záznam ze serveru
 * @param {Date} ted            čas pro test (default teď)
 * @returns {{klic: string, procenta: number|null, plati_do: string|null}}
 *
 * Pořadí větví je záměrné:
 *  - odeslaný dotazník vyhrává nad vším ostatním. Data už jsou u poradce;
 *    že mezitím vypršel odkaz, na tom nic nemění.
 *  - zneplatnění je vědomý krok poradce, proto stojí nad automatickým vypršením.
 */
export function odvodStav(polozka, ted = new Date()) {
  if (!polozka) return { klic: STAV.NEODESLANO, procenta: null, plati_do: null };

  const platiDo = polozka.plati_do || null;
  const procenta = procentaVyplneni(polozka);

  if (polozka.odeslano_klientem) return { klic: STAV.VYPLNENO, procenta, plati_do: platiDo };
  if (polozka.zneplatneno) return { klic: STAV.ZNEPLATNEN, procenta, plati_do: platiDo };

  const d = datum(platiDo);
  /* Nečitelné datum NEZNAMENÁ vypršelo. Server je jediná autorita nad tím,
     jestli se odkaz otevře; tenhle odznak je informace pro poradce. Falešné
     „vypršelo“ by ho donutilo posílat nový odkaz úplně zbytečně. */
  if (d && d.getTime() <= ted.getTime()) return { klic: STAV.VYPRSEL, procenta, plati_do: platiDo };

  const rozpracovano = !!polozka.posledni_ulozeni || (Number(polozka.vyplnenych_poli) > 0);
  return { klic: rozpracovano ? STAV.ROZPRACOVANO : STAV.CEKA, procenta, plati_do: platiDo };
}

/**
 * Nejnovější dotazník ze seznamu — ten, který rozhoduje o stavu karty.
 *
 * PROČ SE ŘADÍ TADY A NE JEN NA SERVERU:
 * „Nejnovější je první“ je kontrakt, ale kontrakt s cizím kódem se dá porušit
 * (jiné pořadí v databázi, cache, budoucí refaktor). Cena za porušení je
 * vysoká a tichá: karta ukáže stav STARŠÍHO dotazníku, typicky „Odkaz
 * vypršel“, poradce vyrobí a pošle druhý odkaz — a klient, který zrovna
 * vyplňuje ten první, začíná od začátku. Řazení je tu proto pojistka.
 *
 * Když čas vytvoření nezná NIKDO, zůstane pořadí ze serveru (nic si
 * nedomýšlíme). Položky bez čitelného času jdou za ty s časem.
 *
 * @param {Array|null} polozky
 * @returns {object|null}
 */
export function nejnovejsi(polozky) {
  if (!Array.isArray(polozky)) return null;
  const platne = polozky.filter(Boolean);
  if (!platne.length) return null;

  const s_casem = platne.map((p, i) => ({ p, i, t: datum(p.vytvoreno)?.getTime() ?? null }));
  if (s_casem.every((x) => x.t === null)) return platne[0];

  s_casem.sort((a, b) => {
    if (a.t === null && b.t === null) return a.i - b.i;
    if (a.t === null) return 1;
    if (b.t === null) return -1;
    if (b.t !== a.t) return b.t - a.t;
    return a.i - b.i;
  });
  return s_casem[0].p;
}

/** Kolik dní zbývá do vypršení (0 = dnes, null = neznámé). Záporné = po termínu. */
export function zbyvaDni(iso, ted = new Date()) {
  const d = datum(iso);
  if (!d) return null;
  const den = 24 * 60 * 60 * 1000;
  return Math.ceil((d.getTime() - ted.getTime()) / den);
}

/* ────────────────────────────────────────────────────────────────────────────
 * MASKOVÁNÍ
 * ──────────────────────────────────────────────────────────────────────────*/

/** Je pole vyplněné? Nula JE odpověď (příjem 0 Kč), prázdný řetězec není. */
export function jeVyplneno(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Částka k zobrazení: 48000 → „48 000“. Nic jiného než čisté číslo se
 * NEPŘEPISUJE — vrací se přesně to, co klient napsal.
 *
 * PROČ tak opatrně: do pole s částkou lidi píšou „48 000 – 52 000“, „cca 48k“
 * nebo „48000 + provize“. Kdyby se to protlačilo přes Number(), vyšlo by NaN
 * nebo useknutá hodnota a v dokumentu pro banku by stálo číslo, které klient
 * nikdy neuvedl. Formátuje se proto jen jednoznačný zápis, zbytek jde beze změny.
 *
 * Týká se JEN typu „castka“. IČO nebo číslo dokladu jsou text a formátování by
 * je poškodilo (01234567 → 1 234 567).
 */
export function formatujCastku(hodnota, typ, lang = "cs") {
  if (typ !== "castka") return null;
  const s = String(hodnota ?? "").trim();
  if (!/^-?\d+([.,]\d{1,2})?$/.test(s)) return null;
  const cislo = Number(s.replace(",", "."));
  if (!Number.isFinite(cislo)) return null;
  return new Intl.NumberFormat(lang === "sk" ? "sk-SK" : "cs-CZ").format(cislo);
}

const ODRAZKY = (n) => "•".repeat(Math.max(1, n));

/* Tvary, které vyrábíme sami. Server posílá citlivá pole už zamaskovaná a
 * frontend je maskuje ZNOVU (viz maskujCitlive). Bez téhle výjimky by druhé
 * kolo setřelo i datum narození na samé odrážky a poradce by nepoznal, jestli
 * pole vůbec patří k tomuhle člověku.
 *
 * Propustit jde jen PŘESNÝ maskovaný tvar. Plaintextové rodné číslo ani číslo
 * účtu odrážku neobsahují, takže se přes tuhle větev nepropašují — a hodnota
 * jako 790115/1234• (rozbitý server, útok) sem taky nepasuje. */
const MASKOVANE_TVARY = [/^\d{6}\/•+$/, /^•{4}[0-9A-Za-z]{0,4}$/, /^•+$/];
export const jeJizMaskovano = (v) => MASKOVANE_TVARY.some((r) => r.test(String(v ?? "")));

/**
 * Rodné číslo → maskovaný tvar „790115/••••“.
 *
 * PROČ SE UKAZUJE PRVNÍCH ŠEST ČÍSLIC A KONCOVKA NE:
 * Prvních šest je datum narození, které poradce stejně zná (má ho na kartě
 * klienta i jako samostatné pole dotazníku) — díky nim pozná, že pole patří
 * k tomuhle člověku a je vyplněné smysluplně. Teprve koncovka dělá z data
 * narození celostátní identifikátor, kterým se dá jednat jménem klienta.
 * Právě ta na obrazovce svítit nesmí.
 *
 * Neznámý tvar (ne 9–10 číslic) maskujeme CELÝ. Když si nejsme jistí, co je
 * v poli, nesmíme z něj ukázat nic — mohl tam klient vepsat cokoliv.
 *
 * @returns {string|null} maskovaný text, nebo null když pole není vyplněné
 */
export function maskujRodneCislo(hodnota) {
  if (!jeVyplneno(hodnota)) return null;
  if (jeJizMaskovano(hodnota)) return String(hodnota);
  const cislice = String(hodnota).replace(/\D/g, "");
  if (cislice.length < 9 || cislice.length > 10) return ODRAZKY(4);
  return `${cislice.slice(0, 6)}/${ODRAZKY(cislice.length - 6)}`;
}

/**
 * Obecný citlivý údaj (číslo dokladu, číslo účtu) → „••••1234“.
 * Stejný tvar jako mask_account() na backendu, ať poradce vidí v CRM a v exportu
 * tentýž zápis a nemusí přemýšlet, jestli se dívá na jinou hodnotu.
 */
export function maskujCislo(hodnota) {
  if (!jeVyplneno(hodnota)) return null;
  if (jeJizMaskovano(hodnota)) return String(hodnota);
  const znaky = String(hodnota).replace(/[^0-9a-zA-Z]/g, "");
  if (znaky.length <= 4) return ODRAZKY(4);
  return `${ODRAZKY(4)}${znaky.slice(-4)}`;
}

/**
 * Zamaskuje citlivé pole podle jeho id.
 *
 * Volá se na VŠECHNO, co má v definici příznak `citlive`, i kdyby už server
 * poslal hodnotu maskovanou. Je to pojistka proti tomu, aby jediná chyba na
 * backendu (nebo nasazení starší verze routy) vysypala rodné číslo na
 * obrazovku. Už zamaskovaná hodnota projde beze změny (jeJizMaskovano),
 * cokoliv jiného se zamaskuje znovu.
 */
export function maskujCitlive(poleId, hodnota) {
  if (String(poleId || "").includes("rodne_cislo")) return maskujRodneCislo(hodnota);
  return maskujCislo(hodnota);
}
